import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { createAuthAPI } from '../../utils/api';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingDown } from 'lucide-react';

const WeightTracker = ({ profile }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const loadRecords = async () => {
      try {
        const api = createAuthAPI();
        const response = await api.get('/client/weight-records', {
          signal: abortController.signal
        });
        if (isMounted) {
          setRecords(response.data);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && isMounted) {
          toast.error('Error al cargar registros');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadRecords();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  if (loading) {
    return <div>Cargando progreso...</div>;
  }

  const chartData = [...records].reverse().map(record => ({
    fecha: new Date(record.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
    peso: record.peso,
    glucemia: record.glucemia,
    colesterol_total: record.colesterol,
    trigliceridos: record.trigliceridos,
    hdl: record.colesterol_hdl,
    ldl: record.colesterol_ldl
  }));

  const currentWeight = records[0]?.peso || profile?.peso_inicial;
  const initialWeight = profile?.peso_inicial;
  const weightLoss = initialWeight && currentWeight ? initialWeight - currentWeight : 0;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-green-800">Mi Progreso</h2>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="pharmacy-card">
          <CardContent className="pt-6">
            <p className="text-sm text-gray-600">Peso Inicial</p>
            <p className="text-3xl font-bold text-gray-800">{initialWeight} kg</p>
          </CardContent>
        </Card>
        <Card className="pharmacy-card">
          <CardContent className="pt-6">
            <p className="text-sm text-gray-600">Peso Actual</p>
            <p className="text-3xl font-bold text-green-600">{currentWeight?.toFixed(1)} kg</p>
          </CardContent>
        </Card>
        <Card className="pharmacy-card bg-gradient-to-r from-green-50 to-emerald-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <TrendingDown className="h-6 w-6 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Pérdida Total</p>
                <p className="text-3xl font-bold text-green-600">{weightLoss.toFixed(1)} kg</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weight Chart */}
      {chartData.length > 0 && (
        <>
          <Card className="pharmacy-card">
            <CardHeader>
              <CardTitle className="text-green-800">📊 Evolución del Peso</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis 
                    dataKey="fecha" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    domain={['dataMin - 2', 'dataMax + 2']}
                    tick={{ fontSize: 12 }}
                    label={{ value: 'kg', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                    labelStyle={{ fontWeight: 'bold' }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} />
                  <Line 
                    type="monotone" 
                    dataKey="peso" 
                    stroke="#66bb6a" 
                    name="Peso (kg)" 
                    strokeWidth={3}
                    dot={{ r: 6, fill: '#66bb6a' }}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Tabla de Peso */}
          <Card className="pharmacy-card">
            <CardHeader>
              <CardTitle className="text-green-800">📋 Tabla de Registros de Peso</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border">
                  <thead className="bg-green-50">
                    <tr>
                      <th className="p-3 border text-left font-semibold">Fecha</th>
                      <th className="p-3 border text-left font-semibold">Peso (kg)</th>
                      <th className="p-3 border text-left font-semibold">Cambio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...records].reverse().map((record, idx, arr) => {
                      const prevWeight = idx > 0 ? arr[idx - 1].peso : null;
                      const change = prevWeight ? (record.peso - prevWeight).toFixed(1) : '-';
                      return (
                        <tr key={record.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 border">{new Date(record.fecha).toLocaleDateString('es-ES')}</td>
                          <td className="p-3 border font-bold text-green-700">{record.peso?.toFixed(1) || '-'}</td>
                          <td className="p-3 border">
                            {change !== '-' && (
                              <span className={parseFloat(change) < 0 ? 'text-green-600 font-semibold' : 'text-red-600'}>
                                {change > 0 ? '+' : ''}{change} kg
                              </span>
                            )}
                            {change === '-' && <span className="text-gray-400">-</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Glucemia Chart */}
      {chartData.some(d => d.glucemia) && (
        <>
          <Card className="pharmacy-card">
            <CardHeader>
              <CardTitle className="text-green-800">📊 Evolución de la Glucemia</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={chartData.filter(d => d.glucemia)} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis 
                    dataKey="fecha" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    domain={[60, 'dataMax + 10']}
                    tick={{ fontSize: 12 }}
                    label={{ value: 'mg/dL', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                    labelStyle={{ fontWeight: 'bold' }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} />
                  <Line 
                    type="monotone" 
                    dataKey="glucemia" 
                    stroke="#9c27b0" 
                    name="Glucemia (mg/dL)" 
                    strokeWidth={3}
                    dot={{ r: 6, fill: '#9c27b0' }}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Tabla de Glucemia */}
          <Card className="pharmacy-card">
            <CardHeader>
              <CardTitle className="text-green-800">📋 Tabla de Glucemia</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 p-3 rounded mb-3">
                <p className="text-sm text-blue-700">
                  <strong>Valor normal:</strong> 70-100 mg/dL (en ayunas)
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border">
                  <thead className="bg-purple-50">
                    <tr>
                      <th className="p-3 border text-left font-semibold">Fecha</th>
                      <th className="p-3 border text-left font-semibold">Glucemia (mg/dL)</th>
                      <th className="p-3 border text-left font-semibold">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...records].reverse().filter(r => r.glucemia).map((record) => {
                      const valor = record.glucemia;
                      const estado = valor < 70 ? 'Bajo' : valor <= 100 ? 'Normal ✓' : valor <= 125 ? 'Elevado' : 'Alto';
                      const color = valor < 70 ? 'text-orange-600' : valor <= 100 ? 'text-green-600 font-semibold' : valor <= 125 ? 'text-yellow-600' : 'text-red-600';
                      return (
                        <tr key={record.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 border">{new Date(record.fecha).toLocaleDateString('es-ES')}</td>
                          <td className="p-3 border font-bold text-purple-700">{valor}</td>
                          <td className={`p-3 border ${color}`}>{estado}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Lipid Profile Chart */}
      {chartData.some(d => d.colesterol_total || d.trigliceridos || d.hdl || d.ldl) && (
        <>
          <Card className="pharmacy-card">
            <CardHeader>
              <CardTitle className="text-green-800">📊 Evolución del Perfil Lipídico</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis 
                    dataKey="fecha" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    domain={[0, 'dataMax + 20']}
                    tick={{ fontSize: 12 }}
                    label={{ value: 'mg/dL', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                    labelStyle={{ fontWeight: 'bold' }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} />
                  {chartData.some(d => d.colesterol_total) && (
                    <Line 
                      type="monotone" 
                      dataKey="colesterol_total" 
                      stroke="#ef5350" 
                      name="Col. Total" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  )}
                  {chartData.some(d => d.trigliceridos) && (
                    <Line 
                      type="monotone" 
                      dataKey="trigliceridos" 
                      stroke="#ff9800" 
                      name="Triglicéridos" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  )}
                  {chartData.some(d => d.hdl) && (
                    <Line 
                      type="monotone" 
                      dataKey="hdl" 
                      stroke="#66bb6a" 
                      name="HDL" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  )}
                  {chartData.some(d => d.ldl) && (
                    <Line 
                      type="monotone" 
                      dataKey="ldl" 
                      stroke="#42a5f5" 
                      name="LDL" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Tabla de Perfil Lipídico */}
          <Card className="pharmacy-card">
            <CardHeader>
              <CardTitle className="text-green-800">📋 Tabla de Perfil Lipídico</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 p-3 rounded mb-3">
                <p className="text-sm text-blue-700 space-y-1">
                  <strong>Valores normales:</strong><br/>
                  • Colesterol Total: &lt; 200 mg/dL<br/>
                  • HDL (bueno): &gt; 40 mg/dL (H) / &gt; 50 (M)<br/>
                  • LDL (malo): &lt; 100 mg/dL<br/>
                  • Triglicéridos: &lt; 150 mg/dL
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border">
                  <thead className="bg-blue-50">
                    <tr>
                      <th className="p-3 border text-left font-semibold">Fecha</th>
                      <th className="p-3 border text-left font-semibold">Col. Total</th>
                      <th className="p-3 border text-left font-semibold">HDL</th>
                      <th className="p-3 border text-left font-semibold">LDL</th>
                      <th className="p-3 border text-left font-semibold">Triglicéridos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...records].reverse().filter(r => r.colesterol || r.colesterol_hdl || r.colesterol_ldl || r.trigliceridos).map((record) => (
                      <tr key={record.id} className="border-b hover:bg-gray-50">
                        <td className="p-3 border">{new Date(record.fecha).toLocaleDateString('es-ES')}</td>
                        <td className="p-3 border font-semibold">{record.colesterol || '-'}</td>
                        <td className="p-3 border font-semibold text-green-600">{record.colesterol_hdl || '-'}</td>
                        <td className="p-3 border font-semibold text-orange-600">{record.colesterol_ldl || '-'}</td>
                        <td className="p-3 border font-semibold text-purple-600">{record.trigliceridos || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Signos Vitales */}
      {records.some(r => r.tension_sistolica || r.tension_diastolica || r.pulso || r.spo2) && (
        <>
          {/* Tensión Arterial */}
          {records.some(r => r.tension_sistolica || r.tension_diastolica) && (
            <Card className="pharmacy-card">
              <CardHeader>
                <CardTitle className="text-green-800">❤️ Tensión Arterial</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-cyan-50 p-3 rounded-lg mb-4 border border-cyan-200">
                  <p className="text-sm text-cyan-900 font-semibold">
                    📊 Valores Normales: Sistólica 90-120 mmHg / Diastólica 60-80 mmHg
                  </p>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[...records].reverse().filter(r => r.tension_sistolica || r.tension_diastolica)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="fecha" 
                      tickFormatter={(value) => new Date(value).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}
                    />
                    <YAxis domain={[40, 200]} />
                    <Tooltip 
                      labelFormatter={(value) => new Date(value).toLocaleDateString('es-ES')}
                      formatter={(value) => [value ? `${value} mmHg` : '-', '']}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="tension_sistolica" stroke="#ef4444" strokeWidth={2} name="Sistólica (máxima)" />
                    <Line type="monotone" dataKey="tension_diastolica" stroke="#3b82f6" strokeWidth={2} name="Diastólica (mínima)" />
                  </LineChart>
                </ResponsiveContainer>

                <div className="mt-6 overflow-x-auto">
                  <table className="w-full border-collapse border">
                    <thead className="bg-cyan-50">
                      <tr>
                        <th className="p-3 border text-left font-semibold">Fecha</th>
                        <th className="p-3 border text-left font-semibold">Sistólica (mmHg)</th>
                        <th className="p-3 border text-left font-semibold">Diastólica (mmHg)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...records].reverse().filter(r => r.tension_sistolica || r.tension_diastolica).map((record, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="p-3 border">{new Date(record.fecha).toLocaleDateString('es-ES')}</td>
                          <td className="p-3 border">{record.tension_sistolica || '-'}</td>
                          <td className="p-3 border">{record.tension_diastolica || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pulso */}
          {records.some(r => r.pulso) && (
            <Card className="pharmacy-card">
              <CardHeader>
                <CardTitle className="text-green-800">💓 Pulso / Frecuencia Cardíaca</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-pink-50 p-3 rounded-lg mb-4 border border-pink-200">
                  <p className="text-sm text-pink-900 font-semibold">
                    📊 Valores Normales: 60-100 latidos por minuto
                  </p>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[...records].reverse().filter(r => r.pulso)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="fecha" 
                      tickFormatter={(value) => new Date(value).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}
                    />
                    <YAxis domain={[40, 120]} />
                    <Tooltip 
                      labelFormatter={(value) => new Date(value).toLocaleDateString('es-ES')}
                      formatter={(value) => [value ? `${value} lpm` : '-', '']}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="pulso" stroke="#ec4899" strokeWidth={2} name="Pulso (lpm)" />
                  </LineChart>
                </ResponsiveContainer>

                <div className="mt-6 overflow-x-auto">
                  <table className="w-full border-collapse border">
                    <thead className="bg-pink-50">
                      <tr>
                        <th className="p-3 border text-left font-semibold">Fecha</th>
                        <th className="p-3 border text-left font-semibold">Pulso (lpm)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...records].reverse().filter(r => r.pulso).map((record, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="p-3 border">{new Date(record.fecha).toLocaleDateString('es-ES')}</td>
                          <td className="p-3 border">{record.pulso}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* SpO2 */}
          {records.some(r => r.spo2) && (
            <Card className="pharmacy-card">
              <CardHeader>
                <CardTitle className="text-green-800">🫁 Saturación de Oxígeno (SpO2)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-blue-50 p-3 rounded-lg mb-4 border border-blue-200">
                  <p className="text-sm text-blue-900 font-semibold">
                    📊 Valores Normales: 95-100%
                  </p>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[...records].reverse().filter(r => r.spo2)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="fecha" 
                      tickFormatter={(value) => new Date(value).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}
                    />
                    <YAxis domain={[85, 100]} />
                    <Tooltip 
                      labelFormatter={(value) => new Date(value).toLocaleDateString('es-ES')}
                      formatter={(value) => [value ? `${value}%` : '-', '']}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="spo2" stroke="#0ea5e9" strokeWidth={2} name="SpO2 (%)" />
                  </LineChart>
                </ResponsiveContainer>

                <div className="mt-6 overflow-x-auto">
                  <table className="w-full border-collapse border">
                    <thead className="bg-blue-50">
                      <tr>
                        <th className="p-3 border text-left font-semibold">Fecha</th>
                        <th className="p-3 border text-left font-semibold">SpO2 (%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...records].reverse().filter(r => r.spo2).map((record, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="p-3 border">{new Date(record.fecha).toLocaleDateString('es-ES')}</td>
                          <td className="p-3 border">{record.spo2}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {records.length === 0 && (
        <Card className="pharmacy-card">
          <CardContent className="py-12 text-center">
            <p className="text-gray-600">Aún no tienes registros de progreso.</p>
            <p className="text-sm text-gray-500 mt-2">
              Tu farmacéutico añadirá los registros de tu evolución.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WeightTracker;